import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

// Constants
const ROWS_PER_PAGE_STORAGE_KEY = "user_rows_per_page_preference";
const DEFAULT_ROWS_PER_PAGE = 500;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100, 500, 1000, 5000, 10000];

// Types
interface PaginationContextType {
  rowsPerPage: number;
  setRowsPerPage: (value: number) => void;
  rowsPerPageOptions: number[];
  isInitialized: boolean;
}

// Create context
const PaginationContext = createContext<PaginationContextType | undefined>(
  undefined,
);

// Provider component
export const PaginationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [rowsPerPage, setRowsPerPageState] = useState<number>(
    DEFAULT_ROWS_PER_PAGE,
  );
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Initialize from localStorage on mount
  useEffect(() => {
    const storedValue = localStorage.getItem(ROWS_PER_PAGE_STORAGE_KEY);
    if (storedValue) {
      const parsedValue = parseInt(storedValue, 10);
      if (ROWS_PER_PAGE_OPTIONS.includes(parsedValue)) {
        setRowsPerPageState(parsedValue);
      }
    }
    setIsInitialized(true);
  }, []);

  // Update localStorage when rowsPerPage changes
  const setRowsPerPage = (value: number) => {
    if (ROWS_PER_PAGE_OPTIONS.includes(value)) {
      setRowsPerPageState(value);
      localStorage.setItem(ROWS_PER_PAGE_STORAGE_KEY, value.toString());
    }
  };

  const value: PaginationContextType = {
    rowsPerPage,
    setRowsPerPage,
    rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
    isInitialized,
  };

  return (
    <PaginationContext.Provider value={value}>
      {children}
    </PaginationContext.Provider>
  );
};

// Custom hook to use pagination context
export const usePagination = (): PaginationContextType => {
  const context = useContext(PaginationContext);
  if (!context) {
    throw new Error("usePagination must be used within PaginationProvider");
  }
  return context;
};
